/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */

import { formatAxisTime } from '@/lib/general-utils';
import { ApiFragment, ExtRegionParams } from '@/types/workbench';
import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WaveForm, WaveSurfer as WaveSurferComponent } from 'wavesurfer-react';
import { PluginType } from 'wavesurfer-react/src/types';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin, { Region, RegionParams } from 'wavesurfer.js/dist/plugins/regions';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import { RegionSelectedColor, RegionProcessedSelectedColor } from './AVDisplay';

export interface ExtRegion extends Region {
  _unsubscribe?(): void;
  _subscribe?(e: MouseEvent): void;
  selected?: boolean;
  locked?: boolean;
  edited?: boolean;
  fragment: ApiFragment;
  regionParams: ExtRegionParams;
}

export interface WaveformProps {
  onComponentLoad?(waveSurfer: WaveSurfer): void;
  regionsVisible?: boolean;
  /** When showing only segments or clips, regions use full waveform height */
  waveformViewMode?: 'segments' | 'clips';
}

const Waveform: FC<WaveformProps> = ({ onComponentLoad, regionsVisible, waveformViewMode }) => {
  const videoRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const wavesurferRef: MutableRefObject<WaveSurfer | null> = useRef(null);
  const regionsContainerRef = useRef<HTMLDivElement | null>(null);
  const waveformViewModeRef = useRef(waveformViewMode);
  const [isExpanded, setIsExpanded] = useState(true);

  waveformViewModeRef.current = waveformViewMode;

  const plugins: PluginType<any>[] = useMemo(() => {
    return [
      {
        key: 'regions',
        plugin: RegionsPlugin,
        options: {},
      },
      {
        key: 'top-timeline',
        plugin: TimelinePlugin,
        options: {
          height: 20,
          insertPosition: 'beforebegin',
          style: {
            color: '#888',
            fontSize: '11px',
          },
        },
      },
      ...(!isExpanded
        ? []
        : [
            {
              key: 'bottom-timeline',
              plugin: TimelinePlugin,
              options: {
                height: 20,
                style: {
                  color: '#888',
                  fontSize: '11px',
                },
              },
            },
          ]),
    ];
  }, [isExpanded]);

  useEffect(() => {
    if (!regionsContainerRef.current) return;
    regionsContainerRef.current.style.visibility = regionsVisible ? 'visible' : 'hidden';
  }, [regionsVisible]);

  const REGION_CSS = `
    /* Allow the scroll container to show handles that extend beyond regions vertically */
    .scroll {
      overflow-y: visible !important;
    }
    #regions-container {
      overflow: visible !important;
    }
    [data-region-selected] {
      border: 2px solid transparent;
      box-sizing: border-box;
      transition: border-color 0.2s, background-color 0.2s;
      overflow: visible !important;
    }
    [data-region-selected="true"] {
      border-color: #ffd600 !important;
      background-color: rgba(53, 193, 118, 0.3) !important;
      z-index: 10 !important;
    }

    /* ── Resize handle styling (inside shadow DOM) ── */
    [part~="region-handle-left"],
    [part~="region-handle-right"] {
      display: none;
      pointer-events: auto;
    }
    [data-region-selected="true"] > [part~="region-handle-left"],
    [data-region-selected="true"] > [part~="region-handle-right"] {
      display: flex !important;
      width: 14px !important;
      max-width: 14px !important;
      height: 30px !important;
      background-color: #ffd600 !important;
      border: 1px solid #272727 !important;
      border-radius: 3px !important;
      opacity: 1 !important;
      top: 50% !important;
      transform: translateY(-50%);
      cursor: col-resize !important;
      align-items: center;
      justify-content: center;
      z-index: 11 !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      user-select: none;
      position: absolute;
      pointer-events: auto !important;
      transition: transform 0.1s;
    }
    [data-region-selected="true"] > [part~="region-handle-left"]:hover,
    [data-region-selected="true"] > [part~="region-handle-right"]:hover {
      transform: translateY(-50%) scale(1.1);
    }
    [data-region-selected="true"] > [part~="region-handle-left"] {
      left: -7px !important;
    }
    [data-region-selected="true"] > [part~="region-handle-right"] {
      right: -7px !important;
    }

    .region-time-start,
    .region-time-end {
      position: absolute;
      bottom: 4px;
      padding: 2px 6px;
      font-family: system-ui, -apple-system, sans-serif;
      font-size: 10px;
      font-weight: 600;
      color: #ffd600;
      background: rgba(0, 0, 0, 0.55);
      border-radius: 3px;
      pointer-events: none;
      white-space: nowrap;
      z-index: 12;
      opacity: 0;
      transition: opacity 0.2s;
      letter-spacing: 0.02em;
    }
    [data-region-selected="true"] .region-time-start,
    [data-region-selected="true"] .region-time-end {
      opacity: 1;
    }
    .region-time-start { left: 4px; }
    .region-time-end { right: 4px; }
  `;

  const injectRegionStyles = useCallback((waveSurfer: WaveSurfer) => {
    const wrapper = waveSurfer.getWrapper();
    const root = wrapper.getRootNode();
    const target = root instanceof ShadowRoot ? root : document.head;
    const styleId = 'eos-wavesurfer-region-styles';
    const existing = target.querySelector(`#${styleId}`);
    if (existing) {
      existing.textContent = REGION_CSS;
    } else {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = REGION_CSS;
      target.appendChild(style);
    }
  }, []);

  const HANDLE_STYLES: Partial<CSSStyleDeclaration> = {
    width: '14px',
    maxWidth: '14px',
    height: '30px',
    backgroundColor: '#ffd600',
    border: '1px solid #272727',
    borderRadius: '3px',
    opacity: '1',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '11',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    userSelect: 'none',
    position: 'absolute',
    visibility: 'visible',
    pointerEvents: 'auto',
  };

  const styleHandle = (handle: HTMLElement, side: 'left' | 'right'): void => {
    Object.assign(handle.style, HANDLE_STYLES);
    if (side === 'left') {
      handle.style.left = '-7px';
      handle.style.right = '';
    } else {
      handle.style.right = '-7px';
      handle.style.left = '';
    }
    if (!handle.querySelector('.eos-handle-grip')) {
      const grip = document.createElement('span');
      grip.className = 'eos-handle-grip';
      Object.assign(grip.style, {
        display: 'block',
        height: '11px',
        width: '5px',
        borderLeft: '1.5px solid #070709',
        borderRight: '1.5px solid #070709',
        boxSizing: 'border-box',
        pointerEvents: 'none',
      });
      handle.appendChild(grip);
    }
  };

  const getHandles = (
    regionEl: HTMLElement,
  ): { left: HTMLElement | null; right: HTMLElement | null } => {
    // Try multiple selector strategies to find WaveSurfer handle elements
    let left = regionEl.querySelector<HTMLElement>('[part*="region-handle-left"]');
    let right = regionEl.querySelector<HTMLElement>('[part*="region-handle-right"]');
    if (!left || !right) {
      // Fallback: WaveSurfer handles are direct children with cursor ew-resize
      const children = Array.from(regionEl.children) as HTMLElement[];
      for (const child of children) {
        if (
          child.style.cursor === 'ew-resize' ||
          child.getAttribute('part')?.includes('region-handle')
        ) {
          const partAttr = child.getAttribute('part') || '';
          if (partAttr.includes('left') || (!left && child.style.left === '0px')) {
            left = child;
          } else if (partAttr.includes('right') || child.style.right === '0px') {
            right = child;
          }
        }
      }
    }
    return { left, right };
  };

  const applyHandleVisibility = (regionEl: HTMLElement, isSelected: boolean): void => {
    // Ensure the region element itself doesn't clip the handles
    regionEl.style.overflow = 'visible';

    const applyOnce = (): void => {
      const { left: leftHandle, right: rightHandle } = getHandles(regionEl);
      if (leftHandle) {
        if (isSelected) {
          styleHandle(leftHandle, 'left');
        } else {
          leftHandle.style.display = 'none';
        }
      }
      if (rightHandle) {
        if (isSelected) {
          styleHandle(rightHandle, 'right');
        } else {
          rightHandle.style.display = 'none';
        }
      }
    };

    // Apply immediately
    applyOnce();
    // Re-apply after short delays to catch handles created asynchronously by WaveSurfer.
    // Guard each callback: if the region was deselected before the timeout fires, skip.
    if (isSelected) {
      const guardedApply = (): void => {
        if (regionEl.getAttribute('data-region-selected') === 'true') applyOnce();
      };
      setTimeout(guardedApply, 0);
      setTimeout(guardedApply, 50);
      setTimeout(guardedApply, 150);
    }
  };

  const handleWSMount = useCallback(
    (waveSurfer: WaveSurfer | null) => {
      console.log('Waveform: handleWSMount', waveSurfer);
      if (!waveSurfer) {
        return;
      }
      wavesurferRef.current = waveSurfer;

      // Do not prevent scroll - allow horizontal scrolling to other segments
      // (removed scroll listener that was calling preventDefault/stopPropagation)

      injectRegionStyles(waveSurfer);

      onComponentLoad && onComponentLoad(waveSurfer);

      // Random peaks for placeholder so waveform is visible (avoid flat line from constant 0.02).
      // Duration is huge (24h) so that regions added before the real duration is known don't get
      // their start/end clamped together by WaveSurfer's regions plugin (which would skip
      // addResizeHandles and never reattach them, since setOptions only re-adds handles on a
      // resize flip). Positions self-correct on _setTotalDuration when the real audio loads.
      const dummyData = new Array(1000).fill(0).map(() => Math.random() * 0.9 + 0.1);
      waveSurfer.load('', [dummyData], 60 * 60 * 24).catch(console.error);

      waveSurfer.on('loading', (data) => {
        console.log('loading --> ', data);
      });

      waveSurfer.on('error', (data) => {
        console.log('error --> ', data);
      });

      const rp = waveSurfer.getActivePlugins()[0] as any;
      rp.___addRegion = rp.addRegion;

      console.log('WaveSurfer regions', rp.regions);

      rp.addRegion = function (regionParams: ExtRegionParams) {
        const result = rp.___addRegion(regionParams as RegionParams) as ExtRegion;
        result.locked = regionParams.locked;

        const originalSetOptions = result.setOptions.bind(result);

        // WaveSurfer regions plugin (v7.10.3) only attaches resize handles to a region
        // when start !== end AT CONSTRUCTION TIME. If addRegion fires before WaveSurfer's
        // duration is known (getDuration() returns 0), the constructor's clampPosition
        // collapses start/end to a marker and skips addResizeHandles. Later setOptions
        // calls that fix start/end do NOT re-attach handles unless `resize` itself flips.
        // Workaround: detect non-marker regions that are missing handles, and force a
        // resize false→true toggle so the plugin attaches them.
        const ensureHandlesAttached = (): void => {
          const el = result.element;
          if (!el) return;
          if (result.start === result.end) return;
          if (!regionParams.resize) return;
          if (el.querySelector('[part*="region-handle-left"]')) return;
          originalSetOptions({ resize: false });
          originalSetOptions({ resize: true });
        };

        const updateElementAttributes = (isSelected: boolean): void => {
          const el = result.element;
          if (el) {
            el.setAttribute('data-region-selected', isSelected ? 'true' : 'false');
            applyHandleVisibility(el, isSelected);
          }
        };

        const isClip = regionParams.fragment.type === 'clip';
        // When waveformViewMode is set, show single type at full height
        const mode = waveformViewModeRef.current;
        const useFullHeight = mode === 'segments' || mode === 'clips';

        const updateLabels = (): void => {
          const el = result.element;
          if (!el) return;
          const startSpan = el.querySelector('.region-time-start');
          const endSpan = el.querySelector('.region-time-end');
          if (startSpan) startSpan.textContent = formatAxisTime(result.start);
          if (endSpan) endSpan.textContent = formatAxisTime(result.end);
        };

        if (result.element) {
          applyHandleVisibility(result.element, !!regionParams.selected);
        }

        // Try to attach handles now (in case duration is already correct).
        ensureHandlesAttached();
        // Try again once WaveSurfer fires ready (totalDuration updated → renderPosition
        // can make a marker into a non-marker, at which point we can attach handles).
        const offReady = waveSurfer.on('ready', () => {
          ensureHandlesAttached();
        });
        result.once('remove', () => offReady());

        setTimeout(() => {
          updateElementAttributes(!!regionParams.selected);

          const regionElement = result.element;
          if (regionElement) {
            if (useFullHeight) {
              regionElement.style.top = '0%';
              regionElement.style.height = '100%';
            } else if (isClip) {
              regionElement.style.top = '66.66%';
              regionElement.style.height = '33.33%';
            } else {
              regionElement.style.top = '0%';
              regionElement.style.height = '33.33%';
            }

            const startLabel = document.createElement('span');
            startLabel.className = 'region-time-start';
            startLabel.setAttribute('aria-hidden', 'true');
            const endLabel = document.createElement('span');
            endLabel.className = 'region-time-end';
            endLabel.setAttribute('aria-hidden', 'true');
            regionElement.appendChild(startLabel);
            regionElement.appendChild(endLabel);
            updateLabels();

            result.on('update', updateLabels);
            result.on('update-end', updateLabels);

            if (regionParams.selected) {
              const guardedUpdate = (): void => {
                if (regionElement.getAttribute('data-region-selected') === 'true') {
                  updateElementAttributes(true);
                }
              };
              setTimeout(guardedUpdate, 80);
              setTimeout(guardedUpdate, 150);
              setTimeout(guardedUpdate, 300);
            }
          }
        }, 0);

        result.setOptions = (options: RegionParams) => {
          originalSetOptions(options);
          // After any setOptions call, start/end may have moved out of the marker
          // state — attach handles if they're still missing.
          ensureHandlesAttached();
          const isSelected =
            options.color === RegionSelectedColor || options.color === RegionProcessedSelectedColor;
          updateElementAttributes(isSelected);
          updateLabels();
          if (isSelected) {
            const el = result.element;
            const guardedUpdate = (): void => {
              if (el?.getAttribute('data-region-selected') === 'true') {
                updateElementAttributes(true);
              }
            };
            setTimeout(guardedUpdate, 50);
            setTimeout(guardedUpdate, 120);
          }
        };

        return result as Region;
      };

      const element = rp.regionsContainer;
      if (element) {
        element.id = 'regions-container';
        regionsContainerRef.current = waveSurfer.getWrapper().querySelector('#regions-container');
      }
    },
    [onComponentLoad],
  );

  // Gradient waveform styling: light pink / warm spectrum, matching reference design
  const expandedWaveColor = [
    'rgba(255,0,255,.2)',
    'rgba(255,0,0,.5)',
    'rgba(255,150,0,.9)',
    'rgba(255,0,0,.5)',
    'rgba(255,0,255,.2)',
  ];

  const expandedProgressColor = [
    'rgba(255,0,255,.2)',
    'rgba(255,0,0,.5)',
    'rgba(255,150,0,.7)',
    'rgba(255,0,0,.5)',
    'rgba(255,0,255,.2)',
  ];

  const collapsedWaveColor = [
    'rgba(255,0,255,.6)',
    'rgba(255,0,0,.8)',
    'rgba(255,150,0,1)',
    'rgba(255,0,0,.8)',
    'rgba(255,0,255,.6)',
  ];

  const collapsedProgressColor = [
    'rgba(255,0,255,.6)',
    'rgba(255,0,0,.8)',
    'rgba(255,150,0,.9)',
    'rgba(255,0,0,.8)',
    'rgba(255,0,255,.6)',
  ];

  const cursorColor = '#606060';

  const waveSurferProps = useMemo(() => {
    const baseOptions = {
      cursorColor,
      normalize: true,
      minPxPerSec: 10,
      fillParent: true,
      backend: 'MediaElement' as const,
      autoScroll: false,
      autoCenter: false,
      dragToSeek: true,
    };

    return isExpanded
      ? {
          ...baseOptions,
          waveColor: expandedWaveColor,
          progressColor: expandedProgressColor,
          height: 64,
          barAlign: undefined,
        }
      : {
          ...baseOptions,
          waveColor: collapsedWaveColor,
          progressColor: collapsedProgressColor,
          height: 20,
          barAlign: 'bottom' as const,
        };
  }, [isExpanded, cursorColor]);

  useEffect(() => {
    wavesurferRef.current?.setOptions(waveSurferProps);
  }, [waveSurferProps]);

  return (
    // Outer container fills parent height as a flex column
    <div
      style={{
        position: 'relative',
        userSelect: 'none',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {!isExpanded && (
        <style>
          {`
            #waveform ::part(regions-container) {
              opacity: 0.5;
            }
          `}
        </style>
      )}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          zIndex: 10,
          padding: '8px 12px',
          fontSize: '14px',
          backgroundColor: 'rgba(0,0,0,0.05)',
          color: '#555',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          minWidth: '36px',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isExpanded ? '▼' : '▲'}
      </button>

      {/* Inner wrapper: minHeight so waveform has space to render bars */}
      <div
        ref={videoRef}
        style={{
          flex: 1,
          minHeight: 160,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <WaveSurferComponent
          plugins={plugins}
          onMount={handleWSMount}
          container="#waveform"
          {...waveSurferProps}
        >
          <WaveForm id="waveform" />
          <div id="timeline" />
        </WaveSurferComponent>
      </div>
    </div>
  );
};

export default Waveform;
